import {
    AdditionalInfoFormView,
    AdditionalInfoType,
    ApiResultGetAdditionalInfosByCursor,
    ApiResultGetScheduledEventInfosByCursor,
    CreateAdditionalInfo,
    DeleteAdditionalInfo,
    GetAdditionalInfosByCursor,
    GetAdditionalInfosByIds,
    GetScheduledEventsAdditionalInfos,
    Priority,
    CreateAdditionainfoNextResponse,
    CreateAdditionalInfoNext,
    AdditionalInfoFormViewNext,
} from '../../types';
import { useApiClient } from '@m43/core';
import {
    ApiResultAdditionalInfosWithLinkedEventMap,
    ApiCadAdditionalInfoDeletionResponse,
    ApiCadAdditionalInfoCreationResponse,
} from '@mark43/cad-api';
import { ListDirection } from './types';
import { AxiosResponse } from 'axios-cad-nx';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default () => {
    const apiClient = useApiClient();

    /**
     * Deletes an Additional Info.
     *
     * @param infoId - ID of the Additional Infos to delete.
     * @returns No value returned.
     * @throws The `AXIOSResponse` of a failed request to delete the Additional Info.
     */
    const deleteInfo = async (
        agencyEventId: number,
        additionalInfoId: number
    ): Promise<AxiosResponse<ApiCadAdditionalInfoDeletionResponse, any>> => {
        return await apiClient.request<DeleteAdditionalInfo>({
            method: 'DELETE',
            url: `v2/events/${agencyEventId}/additional_infos/${additionalInfoId}/`,
            shouldFailSilently: true,
        });
    };

    /**
     * Retrieves a list of Additional Infos for a specified Event.
     *
     * @param eventId - ID of the event to retrieve the Additional Infos for.
     * @param cursor - If cursor is present, the list will start one ID past the cursor (direction is dependant on `direction`).
     *                 If cursor is not present the list will start at the newest or oldest Additional Info, depending on `direction`).
     * @param limit - The maximum number of Additional Infos to return.
     * @param direction - Direction of the list, `HISTORIC` means IDs older than the cursor, `CATCHUP` means IDs newer than the cursor. Default is HISTORIC.
     * @param filter - Determines whether the list should include Additional Infos of type `USER`, `SYSTEM` or `PROQA`.
     * @returns The `AXIOSResponse` of a successful request to get the Additional Infos list.
     * @throws The `AXIOSResponse` of a failed request to get the Additional Infos list.
     */
    const getInfosByCursor = async ({
        agencyEventId,
        limit: pageSize = 100,
        direction = ListDirection.HISTORIC,
        filter = [AdditionalInfoType.USER, AdditionalInfoType.SYSTEM],
        cursor: nextCursor,
        includeEventNarratives = true,
    }: {
        agencyEventId: number;
        limit?: number;
        direction?: ListDirection;
        filter?: Array<AdditionalInfoType>;
        cursor?: number;
        includeEventNarratives?: boolean;
    }): Promise<AxiosResponse<ApiResultGetAdditionalInfosByCursor>> => {
        return await apiClient.request<GetAdditionalInfosByCursor>({
            method: 'GET',
            url: `v2/next/events/${agencyEventId}/additional_infos`,
            params: { nextCursor, pageSize, direction, filter, includeEventNarratives },
            shouldFailSilently: true,
        });
    };

    /**
     * Retrieves the additional infos for a specified ScheduledEvent.
     */
    const getScheduledEventInfos = async ({
        agencyEventId,
    }: {
        agencyEventId: number;
    }): Promise<AxiosResponse<ApiResultGetScheduledEventInfosByCursor>> => {
        return await apiClient.request<GetScheduledEventsAdditionalInfos>({
            method: 'GET',
            url: `v2/next/scheduled_events/${agencyEventId}/additional_infos`,
            shouldFailSilently: true,
        });
    };

    /**
     * Create a new Additional Info/Narrative
     *
     * @param info - The new additional info to be created. Includes agencyEventId, isHighPriority, and text.
     * @returns The `AXIOSResponse` of a successful request to create the additional info.
     * @throws The `AXIOSResponse` of a failed request to create the additional info.
     */
    const createAdditionalInfo = async ({
        agencyEventId,
        text,
        isHighPriority,
        isCrossClass,
    }: AdditionalInfoFormView): Promise<
        AxiosResponse<ApiCadAdditionalInfoCreationResponse, any>
    > => {
        return await apiClient.request<CreateAdditionalInfo>({
            method: 'POST',
            url: `v2/events/${agencyEventId}/additional_infos`,
            data: {
                text,
                priority: isHighPriority ? Priority.HIGH : Priority.STANDARD,
            },
            params: { is_cross_class: isCrossClass },
            shouldFailSilently: true,
        });
    };

    const createAdditionalInfoNext = async ({
        agencyEventId,
        text,
        isHighPriority,
        crossClassOptions,
        toExternalCadIntegrations,
    }: AdditionalInfoFormViewNext): Promise<
        AxiosResponse<CreateAdditionainfoNextResponse, any>
    > => {
        return await apiClient.request<CreateAdditionalInfoNext>({
            method: 'POST',
            url: `v2/additional_infos`,
            data: {
                agencyEventId,
                text,
                priority: isHighPriority ? 'HIGH_PRIORITY_UPDATE' : 'STANDARD_UPDATE',
                crossClassAgencyTypes: crossClassOptions,
                toExternalCadIntegrations,
            },
            shouldFailSilently: true,
        });
    };

    const getInfosByIds = async (
        agencyEventId: number,
        data: { ids: number[] }
    ): Promise<AxiosResponse<ApiResultAdditionalInfosWithLinkedEventMap, any>> => {
        return await apiClient.request<GetAdditionalInfosByIds>({
            method: 'POST',
            url: `v2/next/events/${agencyEventId}/additional_infos/ids`,
            data: data.ids,
            shouldFailSilently: true,
        });
    };

    return {
        createAdditionalInfoNext,
        createAdditionalInfo,
        deleteInfo,
        getInfosByCursor,
        getInfosByIds,
        getScheduledEventInfos,
    };
};
